import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import axios from "axios";

import AcUnitIcon from "@mui/icons-material/AcUnit";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  //Legend,
  ResponsiveContainer,
  //LabelList,
} from "recharts";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import HistoryFilter from "./HistoryFilter";

import "./HistoryGraph.css";

interface props {
  interval: boolean;
  //clientId: any;
  deviceId: any;
  agg: number;
  type: string;
  height: number;
  tools: boolean;
  filter: boolean;
  leftMargin: boolean;
}

// ... [existing imports] ...

const HistoryGraph: React.FC<props> = ({ interval, deviceId, agg, type, height, tools, filter, leftMargin }) => {
  const state = useSelector((state: any) => state.homeState);
  const deviceHistories = state.deviceHistories;

  const [data, setData] = useState<any[]>([]);
  const [filterText, setFilterText] = useState("Last 24 Hours");
  const [graphStart, setGraphStart] = useState(0);
  const [graphEnd, setGraphEnd] = useState(0);
  const [yAxisWidth, setYAxisWidth] = useState(20);

  useEffect(() => {
    const screenWidth = window.innerWidth;
    screenWidth > 768 ? setYAxisWidth(20) : setYAxisWidth(40);
  }, []);

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      requestAnimationFrame(() => {
        for (let entry of entries) {
          // safely trigger any updates
        }
      });
    });

    observer.observe(document.body);
    return () => observer.disconnect();
  }, []);

  const [maxValue, setMaxValue] = useState(0);
  const [minValue, setMinValue] = useState(0);
  const [avgValue, setAvgValue] = useState(0);
  const [powerOffValue, setPowerOffValue] = useState(0);

  const sortFunction = (myData: any) => {
    //max temp
    if (myData.length > 1) {
      setMaxValue(0);
      setMinValue(0);
      setAvgValue(0);
      setPowerOffValue(0);
    }
    const sortedMax = [...myData].sort((a: any, b: any) => b.tempValue - a.tempValue);
    const highestTemp: any = sortedMax[0];
    setMaxValue(highestTemp?.tempValue || 0);

    //min temp
    const sortedMin = [...myData].sort((a: any, b: any) => a.tempValue - b.tempValue);
    const lowestTemp: any = sortedMin[0];
    setMinValue(lowestTemp?.tempValue || 0);

    //average temp
    const keyToAverage = "tempValue";

    const sum = myData.reduce((accumulator: any, currentObject: any) => {
      return accumulator + (currentObject[keyToAverage] || 0);
    }, 0);

    const average = myData.length > 0 ? sum / myData.length : 0;
    setAvgValue(average);

    //number of times device is off
    function countValuesLessThan100(jsonArray: any, key: any) {
      let count = 0;
      for (const item of jsonArray) {
        if (item[key] < 100) {
          count++;
        }
      }
      return count;
    }

    const keyToCount = "powerOn";
    const result = countValuesLessThan100(myData, keyToCount);
    setPowerOffValue(result);
  };

	const [isFiltered, setIsFiltered] = useState(false);

	const serverUrl = `${process.env.REACT_APP_PROTOCOL}://${process.env.REACT_APP_SERVER_HOST}:${process.env.REACT_APP_SERVER_HOST_PORT}`;

	const fetchData = useCallback(
		(
			reqDeviceId: any,
			reqAgg: number,
			reqType: string,
			startDate: any,
			endDate: any
		) => {
			setIsFiltered(true);
			axios
				.post(
					`${serverUrl}/api/device-history`,
					{
						deviceId: reqDeviceId,
						agg: reqAgg,
						type: reqType,
						startDate: startDate,
						endDate: endDate,
					},
					{
						withCredentials: true,
					}
				)
				.then(async function (response) {
					const apiData = await response.data;
					if (apiData.status === "success") {
						const sortedData = [...apiData.data].sort(
							(a, b) =>
								new Date(a.createdAt).getTime() -
								new Date(b.createdAt).getTime()
						);
						setData(sortedData);

						setGraphStart(sortedData[sortedData.length - 1]?.calcLowStart || 0);
						setGraphEnd(sortedData[sortedData.length - 1]?.calcHighEnd || 0);

						if (sortedData.length > 0) {
							sortFunction(sortedData);
						} else {
							sortFunction([
								{ powerOn: 100, tempValue: 0 },
								{ powerOn: 100, tempValue: 0 },
							]);
						}
					} else {
						setData([]);
						sortFunction([
							{ powerOn: 100, tempValue: 0 },
							{ powerOn: 100, tempValue: 0 },
						]);
					}
				})
				.catch(function (error) {
					console.error(error);
					setData([]);
					sortFunction([
						{ powerOn: 100, tempValue: 0 },
						{ powerOn: 100, tempValue: 0 },
					]);
				});
		},
		[serverUrl]
	);

	useEffect(() => {
		if (isFiltered) return; // Do not overwrite with Redux data if user manually filtered

		let apiData: any[] = [];
		if (type === "hour" && deviceHistories?.hour) {
			apiData = deviceHistories.hour[deviceId] || [];
		} else if (type === "24hour" && deviceHistories?.hour24) {
			apiData = deviceHistories.hour24[deviceId] || [];
		}

		if (apiData && apiData.length > 0) {
			const sortedData = [...apiData].sort(
				(a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
			);
			setData(sortedData);

			setGraphStart(sortedData[sortedData.length - 1]?.calcLowStart || 0);
			setGraphEnd(sortedData[sortedData.length - 1]?.calcHighEnd || 0);

			sortFunction(sortedData);
		} else {
			setData([]);
			sortFunction([
				{ powerOn: 100, tempValue: 0 },
				{ powerOn: 100, tempValue: 0 },
			]);
		}
	}, [deviceId, type, deviceHistories, isFiltered]);

	return (
		<div>
			{filter && (
				<Row>
					<Col md={12}>
						<div>
							<HistoryFilter fetchData={fetchData} deviceId={deviceId} setFilterText={setFilterText} />
            </div>
          </Col>
        </Row>
      )}
      <Row className="history-row">
        {filter && (
          <Col md={4}>
            <Row className="device-status-col">
              <Col md={6} xs={6} className="col-status">
                <div style={{ backgroundColor: "#e7cfd1" }} className="device-status-col-item">
                  <div>
                    <img src="/media/icons/Vector.png" alt="power" className="device-temp-hot" style={{ marginRight: "5px" }} />
                    <span>
                      <strong>Max</strong> Temp
                    </span>
                  </div>
                  <div>
                    <span className="temp-status">{maxValue.toFixed(2)}°C</span>
                    <span className="temp-status-sub">{filterText}</span>
                  </div>
                </div>
              </Col>
              <Col md={6} xs={6} className="col-status">
                <div style={{ backgroundColor: "#dae1ed" }} className="device-status-col-item">
                  <div>
                    <img src="/media/icons/Vector1.png" alt="power" className="device-temp-hot" style={{ marginRight: "5px" }} />
                    <span>
                      <strong>Min</strong> Temp
                    </span>
                  </div>
                  <div>
                    <span className="temp-status">{minValue.toFixed(2)}°C</span>
                    <span className="temp-status-sub">{filterText}</span>
                  </div>
                </div>
              </Col>
              <Col md={6} xs={6} className="col-status">
                <div style={{ backgroundColor: "#EEECD4" }} className="device-status-col-item">
                  <div>
                    <img src="/media/icons/BsThermometerHalf.png" alt="power" className="device-temp-hot" />
                    <span>
                      <strong>Average</strong> Temp
                    </span>
                  </div>
                  <div>
                    <span className="temp-status">{avgValue.toFixed(2)}°C</span>
                    <span className="temp-status-sub">{filterText}</span>
                  </div>
                </div>
              </Col>
              <Col md={6} xs={6} className="col-status">
                <div style={{ backgroundColor: "#e7cfd1" }} className="device-status-col-item">
                  <div>
                    <img src="/media/icons/FaPowerOff1.png" alt="power" className="device-temp-hot" style={{ marginRight: "5px" }} />
                    <span>
                      No. of x <strong>Offline</strong>
                    </span>
                  </div>
                  <div>
                    <span className="temp-status">{powerOffValue}</span>
                    <span className="temp-status-sub">{filterText}</span>
                  </div>
                </div>
              </Col>
            </Row>
          </Col>
        )}
        <Col xs={12} md={filter ? 8 : 12} className={filter ? "history-graph-col" : ""}>
          {/* {filter ? (
					<>
						<h5 className="filter-header">Device History</h5>
						<h5 className="filter-header">
							&nbsp;&nbsp;|&nbsp;&nbsp;Filtered by: {filterText}
						</h5>
						<hr />
					</>
				) : null} */}
          <div className={!filter ? "linechart-container" : "linechart-container-height"}>
            <ResponsiveContainer
              width={"100%"}
              height={height}
              style={{
                padding: tools ? "10px 0 0 10px" : "0",
                margin: "0",
                backgroundColor: "#000000",
                borderRadius: "8px",
              }}
            >
              {/* style={{
						padding: "10px 0 2px 0",
						margin: "0",
						backgroundColor: "#222222",
					}} */}
              <LineChart
                data={data}
                margin={leftMargin ? { top: 0, right: 0, left: 7, bottom: 0 } : { top: 0, right: 0, left: 0, bottom: 0 }}
                // style={{
                // 	backgroundColor: "#222222",
                // 	padding: tools ? "15px 0 10px 0" : "0",
                // }}
                accessibilityLayer
                className="mobi-device-graph-div"
              >
                <CartesianGrid strokeDasharray="0 0" />
                {tools ? (
                  <YAxis
                    type="number"
                    domain={[graphStart, graphEnd]}
                    padding={{ top: 2, bottom: 2 }}
                    stroke="#ffffff"
                    width={yAxisWidth}
                    //dataKey="confirmados"
                  />
                ) : (
                  <YAxis
                    type="number"
                    domain={[graphStart, graphEnd]}
                    padding={{ top: 1, bottom: 1 }}
                    style={{ opacity: 0 }}
                    axisLine={false}
                    tickLine={false}
                    width={0}
                    //dataKey="confirmados"
                  />
                )}
                {tools ? (
                  <XAxis
                    dataKey="createdAt"
                    stroke="#ffffff"
                    style={{ opacity: 0 }}
                    height={10}
                    //padding={{ left: 30, right: 30 }}
                  />
                ) : null}
                {tools ? <YAxis /> : null}
                {tools ? (
                  <Tooltip
                    content={({ payload }) => {
                      if (!payload || payload.length === 0) return null;

                      const xAxisValue = payload[0]?.payload?.createdAt;

                      const tempValue = payload[0]?.payload?.tempValue;

                      const tempSet = payload[0]?.payload?.tempSet;

                      const powerOn = payload[0]?.payload?.powerOn;

                      const coolerOn = payload[0]?.payload?.coolerOn;

                      const heaterOn = payload[0]?.payload?.heaterOn;

                      return (
                        <div
                          style={{
                            backgroundColor: "white",
                            padding: "5px",
                            borderRadius: "8px",
                          }}
                        >
                          {xAxisValue && <p>{`${xAxisValue}`}</p>}
                          {tempValue !== undefined && (
                            <p>
                              <strong>Temp: </strong>
                              {`${tempValue}`}
                            </p>
                          )}
                          {tempSet !== undefined && (
                            <p>
                              <strong>Temp Set: </strong>
                              {`${tempSet}`}
                            </p>
                          )}

                          {powerOn !== 100 && <PowerSettingsNewIcon className="device-icon-position" />}
                          {coolerOn === 100 && powerOn === 100 ? <AcUnitIcon className="device-temp-cold device-icon-position" /> : null}
                          {heaterOn === 100 && powerOn === 100 ? <img src="/media/icons/heating.png" alt="heating" className="device-temp-hot device-icon-position" /> : null}
                        </div>
                      );
                    }}
                  />
                ) : null}
                {/*tools ? /*<Legend /> null : null */}

                <Line type="monotone" dataKey={(entry) => Math.min(Math.max(entry.tempValue, graphStart), graphEnd)} stroke="#FFD700" activeDot={{ r: 8 }} dot={false} strokeWidth={2}></Line>

                <Line type="monotone" dataKey="calcLowEnd" stroke="#014e9a" dot={false}></Line>
                <Line type="monotone" dataKey="calcHighStart" stroke="#ff0000" dot={false}></Line>
                <Line type="monotone" dataKey="tempSet" stroke="#32cd32" dot={false}></Line>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Col>
        {/* {filter ? (
				<Col
					xs={12}
					md={2}
				>
					<HistoryFilter
						fetchData={fetchData}
						deviceId={deviceId}
						setFilterText={setFilterText}
					/>
				</Col>
			) : null} */}
      </Row>
    </div>
  );
};

export default HistoryGraph;
